import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSignUp } from "@/hooks/api/auth";
import { isAxiosError } from "@/lib/api";
import { getTenantSlug } from "@/lib/tenant";
import { useAuthStore } from "@/stores/auth";

const slugRe = /^[a-z][a-z0-9-]{2,62}$/;

const schema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(10, "Password must be at least 10 characters"),
  company_name: z.string().min(2, "Required").max(255, "Too long"),
  slug: z
    .string()
    .regex(
      slugRe,
      "3–63 chars, must start with a letter; lowercase letters, digits, or hyphens"
    ),
});

type FormValues = z.infer<typeof schema>;

export default function SignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenantSlug = getTenantSlug();
  const user = useAuthStore((s) => s.user);
  const signup = useSignUp();
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", company_name: "", slug: "" },
  });

  // Sign-up only makes sense at the root domain — tenant subdomain users log in
  if (tenantSlug) return <Navigate to="/login" replace />;
  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (data: FormValues) => {
    setServerErrors({});
    try {
      await signup.mutateAsync(data);
      // The signup response carries a JWT scoped to the new tenant, and
      // `useSignUp` has already stored it in the auth store. Backend routes
      // tenant via `JWT.active_tenant_id` when there's no subdomain (single-
      // domain mode in TenantMembershipMiddleware), so we just go straight
      // to the onboarding wizard on the same host.
      navigate("/onboarding", { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response?.data) {
        const data = err.response.data as Record<string, string | string[]>;
        const fieldErrors: Record<string, string> = {};
        for (const [k, v] of Object.entries(data)) {
          fieldErrors[k] = Array.isArray(v) ? v[0] : v;
        }
        setServerErrors(fieldErrors);
      } else {
        setServerErrors({ detail: t("common.error_generic") });
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.start_trial")}</CardTitle>
          <CardDescription>{t("auth.signup_subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.company_name")}</FormLabel>
                    <FormControl>
                      <Input autoComplete="organization" {...field} />
                    </FormControl>
                    <FormMessage>
                      {serverErrors.company_name ?? null}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.slug")}</FormLabel>
                    <FormControl>
                      <div className="flex items-center">
                        <Input
                          autoComplete="off"
                          className="rounded-r-none border-r-0"
                          {...field}
                        />
                        <span className="flex h-10 items-center rounded-r-md border border-l-0 border-slate-300 bg-surface-alt px-3 text-sm text-slate-600">
                          .tina.kraetd.com
                        </span>
                      </div>
                    </FormControl>
                    <FormDescription>{t("auth.slug_help")}</FormDescription>
                    <FormMessage>{serverErrors.slug ?? null}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.email")}</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage>{serverErrors.email ?? null}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.password")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t("auth.password_help")}</FormDescription>
                    <FormMessage>{serverErrors.password ?? null}</FormMessage>
                  </FormItem>
                )}
              />

              {serverErrors.detail && (
                <div
                  role="alert"
                  className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm text-danger"
                >
                  {serverErrors.detail}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                loading={signup.isPending}
              >
                {t("auth.create_account")}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center text-sm text-slate-600">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-primary hover:underline">
              {t("auth.login")}
            </Link>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            {t("auth.signup_subdomain_note")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

